/**
 * „Zmień cel dzienny" — the target sheet: „Wpisz ręcznie" (opens selected) — four fields, the macro
 * split once all three macros are typed — or „Kalkulator" (R19, `NutritionTargetCalculator`). A save applies
 * from the client's today (R3). Every rule is `model/nutritionTarget`'s and `model/nutritionCalculator`'s.
 *
 * The energy field stays editable with all three macros typed: R2 stores a typed energy as typed,
 * and only an empty one is derived, which the field then shows as its placeholder.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import ink from '~/shared/ui/inkButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import {
  useNutritionCalculatorInputs,
  useNutritionTargetWrite,
  type NutritionTargetWrite,
} from '../lib/useNutrition';
import { NUTRIENTS, targetOf, wholeNumber, withUnit, type NutritionTargetValues } from '../model/nutrition';
import {
  calculatorOutcome,
  EMPTY_TYPED,
  formFromResult,
  INITIAL_CHOICES,
  inputIssues,
  TYPED_INPUT_ORDER,
  type CalculatorChoices,
  type CalculatorTyped,
  type TypedInput,
} from '../model/nutritionCalculator';
import {
  buildTargetRequest,
  capTargetInput,
  formFromTarget,
  requestMatchesTarget,
  targetFormView,
  targetSaveErrorKey,
  TARGET_BOUNDS,
  type TargetField,
  type TargetFieldError,
  type TargetFormValues,
  type TargetRequest,
} from '../model/nutritionTarget';

import { MacroGlyph } from './NutritionGlyphs';
import styles from './NutritionTab.module.css';
import { NutritionTargetCalculator } from './NutritionTargetCalculator';
import calc from './NutritionTargetCalculator.module.css';

const KEY = 'web:clientDetail.nutrition';

interface NutritionTargetDialogProps {
  readonly clientId: number;
  readonly clientName: string;
  readonly target: NutritionTargetValues | null;
  readonly open: boolean;
  readonly onClose: () => void;
  /** The card's one live region: the dialog closes on the save it announces. */
  readonly announce: (message: string) => void;
}

export function NutritionTargetDialog({
  clientId,
  clientName,
  target,
  open,
  onClose,
  announce,
}: NutritionTargetDialogProps) {
  const { t } = useTranslation();
  // Held here, not in the popup, so a save in flight keeps the dialog open: Esc, the ✕ and the
  // backdrop would otherwise unmount the popup and its outcome would never be told.
  const write = useNutritionTargetWrite(clientId);
  const close = () => {
    if (write.saving) return;
    write.reset();
    onClose();
  };
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={`${String(surface.backdrop)} ${String(styles.targetBackdrop)}`} />
        <TargetPopup
          clientId={clientId}
          clientName={clientName}
          target={target}
          write={write}
          onClose={close}
          onSaved={() => {
            announce(t(`${KEY}.dialog.saved`));
            write.reset();
            onClose();
          }}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The popup and its form state: mounted only while the dialog is open, so it opens fresh. */
function TargetPopup({
  clientId,
  clientName,
  target,
  write,
  onClose,
  onSaved,
}: {
  readonly clientId: number;
  readonly clientName: string;
  readonly target: NutritionTargetValues | null;
  readonly write: NutritionTargetWrite;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [values, setValues] = useState<TargetFormValues>(() => formFromTarget(target));
  const [errors, setErrors] = useState<Readonly<Partial<Record<TargetField, TargetFieldError>>>>({});
  const [energyAttempted, setEnergyAttempted] = useState(false);
  const inputs = useRef<Partial<Record<TargetField, HTMLInputElement | null>>>({});
  const ids = useId();
  const incompleteId = `${ids}-incomplete`;
  const overCapId = `${ids}-over-cap`;
  const view = targetFormView(values);
  // Stamped on a refused save; the mark itself follows the fields, so energy or the third macro clears it.
  const energyRequired = energyAttempted && view.energyMissing;
  const first = target === null;
  const [mode, setMode] = useState<'calc' | 'manual'>('manual');
  const [overCapAnnouncement, setOverCapAnnouncement] = useState('');
  const overCapSoft = mode === 'manual' && view.energyOverCap;
  const energyMax = wholeNumber(TARGET_BOUNDS.energy[1], locale);
  const [typed, setTyped] = useState<CalculatorTyped>(EMPTY_TYPED);
  const [choices, setChoices] = useState<CalculatorChoices>(INITIAL_CHOICES);
  const [calcOutOfBounds, setCalcOutOfBounds] = useState(false);
  const [calcAttempted, setCalcAttempted] = useState(false);
  const [calcAnnouncement, setCalcAnnouncement] = useState('');
  const calcFields = useRef<Partial<Record<TypedInput, HTMLElement | null>>>({});
  const calculatorInputs = useNutritionCalculatorInputs(clientId);
  const outcome =
    calculatorInputs.profile === null ? null : calculatorOutcome(calculatorInputs.profile, typed, choices);
  const calcResult = outcome?.kind === 'ready' ? outcome.result : null;
  const calcIncomplete = mode === 'calc' && outcome?.kind === 'incomplete';
  const saving = write.saving;

  const commit = (request: TargetRequest) => {
    // Nothing changed: a save would only add a version and move „od" to today.
    if (requestMatchesTarget(request, target)) {
      onClose();
      return;
    }
    write.save(request, onSaved);
  };

  const set = (field: TargetField, value: string) => {
    if (saving) return;
    const next = capTargetInput(field, values[field], value);
    if (next === values[field]) return;
    setValues((current) => ({ ...current, [field]: next }));
    setOverCapAnnouncement('');
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    write.reset();
  };

  const fieldError = (field: TargetField): string | null => {
    if (field === 'energy' && energyRequired) return t(`${KEY}.dialog.energyRequired`);
    const error = errors[field];
    if (error === undefined) return null;
    if (error === 'notWhole') return t(`${KEY}.dialog.fieldNotWhole`);
    const [low, high] = TARGET_BOUNDS[field];
    if (field === 'energy' && view.derivedEnergy === null) {
      return t(`${KEY}.dialog.energyMin`, { min: wholeNumber(low, locale) });
    }
    if (field === 'energy' && view.derivedEnergy !== null) {
      return t(`${KEY}.dialog.derivedOutOfRange`, { low: wholeNumber(low, locale), high: wholeNumber(high, locale) });
    }
    return t(`${KEY}.dialog.fieldOutOfRange`, {
      low: wholeNumber(low, locale),
      high: wholeNumber(high, locale),
      unit: field === 'energy' ? 'kcal' : 'g',
    });
  };

  let refusal: string | null = null;
  if (mode === 'calc' && calcOutOfBounds) refusal = t(`${KEY}.calc.outOfBounds`);
  else if (write.failed) refusal = t(targetSaveErrorKey(write.errorCode ?? undefined));

  const calcChanged =
    <T,>(apply: (next: T) => void) =>
    (next: T) => {
      if (saving) return;
      apply(next);
      setCalcOutOfBounds(false);
      setCalcAnnouncement('');
      write.reset();
    };

  const submitCalculation = () => {
    if (calcResult === null) {
      if (calculatorInputs.profile === null) return;
      const issues = inputIssues(calculatorInputs.profile, typed, choices.formula);
      // Committed before the move, so the input is focused already marked and described.
      flushSync(() => {
        setCalcAttempted(true);
        setCalcAnnouncement(t(`${KEY}.calc.fillRequired`));
      });
      const first = TYPED_INPUT_ORDER.find((input) => issues[input] !== undefined);
      if (first !== undefined) calcFields.current[first]?.focus();
      return;
    }
    const request = buildTargetRequest(formFromResult(calcResult)).request;
    if (request === null) {
      setCalcOutOfBounds(true);
      return;
    }
    commit(request);
  };

  const moveToFields = () => {
    if (calcResult === null || saving) return;
    setValues(formFromResult(calcResult));
    setErrors({});
    write.reset();
    setMode('manual');
  };

  const switchMode = (next: 'calc' | 'manual') => {
    if (saving) return;
    write.reset();
    setCalcOutOfBounds(false);
    setOverCapAnnouncement('');
    setMode(next);
  };

  const submit = () => {
    if (mode === 'calc') {
      submitCalculation();
      return;
    }
    const submission = buildTargetRequest(values);
    if (submission.request !== null) {
      setErrors({});
      commit(submission.request);
      return;
    }
    // Committed before the move, so the field is focused already marked and described
    // (SC 3.3.1): the Save button would otherwise keep focus and nothing would be said.
    flushSync(() => {
      setErrors(submission.fieldErrors);
      setEnergyAttempted(submission.energyRequired);
      setOverCapAnnouncement(
        submission.energyOverCap ? t(`${KEY}.dialog.lowerMacros`, { max: energyMax }) : '',
      );
    });
    const invalid =
      NUTRIENTS.find((field) => submission.fieldErrors[field] !== undefined) ??
      (submission.energyOverCap ? 'protein' : 'energy');
    inputs.current[invalid]?.focus();
  };

  return (
    <Dialog.Popup
      className={`${String(surface.popup)} ${String(form.dialog)} ${String(styles.targetDialog)}`}
      initialFocus={() => inputs.current.energy ?? true}
    >
      <div className={form.dialogHead}>
        <div>
          <span className={form.eyebrow}>{clientName}</span>
          <Dialog.Title className={form.dialogTitle}>
            {t(first ? `${KEY}.dialog.titleSet` : `${KEY}.dialog.titleChange`)}
          </Dialog.Title>
        </div>
        <Dialog.Close className={form.close} aria-label={t(`${KEY}.dialog.close`)}>
          <CloseGlyph className={form.closeGlyph} />
        </Dialog.Close>
      </div>

      <div className={calc.mode} role="group" aria-label={t(`${KEY}.calc.modeLabel`)}>
        {(['manual', 'calc'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={calc.modeButton}
            aria-pressed={mode === option}
            onClick={() => {
              switchMode(option);
            }}
          >
            {t(option === 'calc' ? `${KEY}.calc.modeCalc` : `${KEY}.calc.modeManual`)}
          </button>
        ))}
      </div>

      <form
        className={styles.targetForm}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {mode === 'calc' ? (
          <NutritionTargetCalculator
            inputs={calculatorInputs}
            typed={typed}
            onTyped={calcChanged(setTyped)}
            choices={choices}
            onChoices={calcChanged(setChoices)}
            outcome={outcome}
            attempted={calcAttempted}
            fieldRefs={calcFields}
          />
        ) : (
          <>
            <div className={styles.targetGrid}>
              {NUTRIENTS.map((field) => {
                const derived = field === 'energy' && view.derivedEnergy !== null;
                const overCap = field === 'energy' && view.energyOverCap;
                const unit = field === 'energy' ? 'kcal' : 'g';
                const previous = targetOf(target, field);
                const error = overCap ? t(`${KEY}.dialog.derivedOverCap`, { max: energyMax }) : fieldError(field);
                const inputId = `${ids}-${field}`;
                const errorId = `${ids}-${field}-error`;
                const describedBy = error !== null ? errorId : undefined;
                return (
                  <div className={form.field} key={field}>
                    <label className={`${String(form.fieldLabel)} ${String(styles.targetLabel)}`} htmlFor={inputId}>
                      <MacroGlyph nutrient={field} small />
                      {t(`${KEY}.nutrient.${field}`)}
                      {previous === null ? null : (
                        <small className={styles.targetWas}>
                          {t(`${KEY}.dialog.was`, { value: withUnit(previous, unit, locale) })}
                        </small>
                      )}
                    </label>
                    <span
                      className={`${String(styles.targetInput)} ${derived ? String(styles.targetInputDerived) : ''} ${
                        overCap ? String(styles.targetInputDanger) : ''
                      }`}
                    >
                      <input
                        aria-describedby={describedBy}
                        aria-invalid={describedBy === undefined ? undefined : true}
                        className={styles.targetInputField}
                        id={inputId}
                        ref={(node) => {
                          inputs.current[field] = node;
                        }}
                        inputMode="numeric"
                        onChange={(event) => {
                          set(field, event.target.value);
                        }}
                        readOnly={saving}
                        placeholder={
                          derived && view.derivedEnergy !== null
                            ? wholeNumber(view.derivedEnergy, locale)
                            : t(
                                field === 'energy'
                                  ? `${KEY}.dialog.placeholderEnergy`
                                  : `${KEY}.dialog.placeholderMacro`,
                              )
                        }
                        value={values[field]}
                      />
                      <em className={styles.targetUnit}>{unit}</em>
                    </span>
                    {error === null ? null : (
                      <p className={styles.targetFieldError} id={errorId}>
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {view.split === null || view.macroEnergy === null ? null : (
              <div className={styles.targetSplit}>
                <div className={styles.splitBar} aria-hidden="true">
                  {view.split.map((part) => (
                    <i
                      className={`${String(styles.splitPart)} ${String(SPLIT_CLASS[part.nutrient])}`}
                      key={part.nutrient}
                      style={{ flexGrow: part.share }}
                    />
                  ))}
                </div>
                <div className={styles.splitLabels}>
                  {view.split.map((part) => (
                    <span key={part.nutrient}>
                      {t(`${KEY}.dialog.split.${part.nutrient}`, { percent: part.percent })}
                    </span>
                  ))}
                </div>
                <p className={`${String(form.note)} ${String(styles.targetHint)}`}>
                  {view.derivedEnergy === null
                    ? t(`${KEY}.dialog.macroSumHint`, { value: wholeNumber(view.macroEnergy, locale) })
                    : t(`${KEY}.dialog.derivedHint`, { value: wholeNumber(view.derivedEnergy, locale) })}
                </p>
              </div>
            )}
          </>
        )}

        {mode === 'calc' ? (
          <>
            <p className={hidden.srOnly} aria-live="polite">
              {calcAnnouncement}
            </p>
            <span className={hidden.srOnly} id={incompleteId}>
              {t(`${KEY}.calc.fillRequired`)}
            </span>
          </>
        ) : (
          <>
            <p className={hidden.srOnly} aria-live="polite">
              {overCapAnnouncement}
            </p>
            <span className={hidden.srOnly} id={overCapId}>
              {t(`${KEY}.dialog.lowerMacros`, { max: energyMax })}
            </span>
          </>
        )}

        {refusal === null ? null : (
          <p className={form.refusal} role="alert">
            {refusal}
          </p>
        )}

        <div className={form.dialogActs}>
          <span className={`${String(form.actReason)} ${String(styles.targetHint)}`}>
            {t(`${KEY}.dialog.effective`)}
          </span>
          <button type="button" className={surface.ghost} onClick={onClose}>
            {t(`${KEY}.dialog.cancel`)}
          </button>
          {mode === 'calc' ? (
            <button
              type="button"
              className={`${String(ink.ink)} ${String(calc.useButton)}`}
              aria-disabled={calcResult === null || saving}
              onClick={moveToFields}
            >
              {t(`${KEY}.calc.use`)}
            </button>
          ) : null}
          <button
            type="submit"
            className={`${String(surface.primary)} ${
              calcIncomplete || overCapSoft ? String(calc.saveSoft) : ''
            }`}
            aria-busy={saving}
            aria-disabled={mode === 'calc' && calculatorInputs.profile === null ? true : undefined}
            aria-describedby={calcIncomplete ? incompleteId : overCapSoft ? overCapId : undefined}
          >
            {t(saving ? `${KEY}.dialog.saving` : `${KEY}.dialog.save`)}
          </button>
        </div>
      </form>
    </Dialog.Popup>
  );
}

const SPLIT_CLASS = {
  protein: styles.splitProtein,
  carbs: styles.splitCarbs,
  fat: styles.splitFat,
} as const;
